import { forwardRef, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { fieldSquareVariants } from '../field/field.tokens';
import { Input } from '../Input/Input';

import { type ColorInputProps } from './ColorInput.types';

/** A complete six-digit hex colour. Three-digit shorthand is deliberately not accepted. */
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * A colour swatch beside a typed hex field — the control behind every
 * `type: 'color'` setting in the registry (brand colours, badge tints).
 *
 * The subtlety is the DRAFT. A colour is only a colour when all six digits are
 * there, so the hex field keeps its own in-progress text and only calls
 * `onValueChange` once the text parses. Without that, every keystroke of
 * "#c9a86a" fires four invalid values at the form and the swatch strobes
 * through black on the way. The field also never rewrites what the operator is
 * typing — a control that fights the person typing in it is worse than no
 * control.
 *
 * Values are normalised to UPPER case on the way out so two settings written on
 * different days do not differ only by letter case.
 *
 * The two controls carry DISTINCT accessible names ("Brand (colour picker)" and
 * "Brand (hex value)"). Giving both the bare field label made a screen reader
 * announce "Brand" twice with nothing to tell them apart — caught by this
 * component's own spec, which could not find either one unambiguously.
 *
 * **The ref goes to the HEX field, not to the swatch** (P10.2d, closing one of
 * the three kinds that took no ref at all): `shouldFocusError` should land where
 * the value is typed and where an invalid one is fixed. The native swatch cannot
 * even hold an invalid value.
 */
export const ColorInput = forwardRef<HTMLInputElement, ColorInputProps>(function ColorInput(
  {
    label,
    value,
    onValueChange,
    isInvalid = false,
    disabled = false,
    size = 'md',
    className,
    id,
    describedById,
    onBlur,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const generatedId = useId();
  const swatchId = id ?? generatedId;

  // The in-progress text. `null` means "not editing" — the hex field then shows
  // the committed `value`, so an update from outside is picked up immediately.
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (next: string) => {
    setDraft(next);
    if (HEX_PATTERN.test(next)) {
      onValueChange(next.toUpperCase());
    }
  };

  return (
    // The description goes on the two CONTROLS, not on this group. A group's
    // description is not reliably announced on entry, so an operator typing in
    // the hex field would never hear it — and unlike `KeyValueEditor` or
    // `PermissionChecklist`, whose rows are dynamic and would repeat one
    // description N times, this group is always exactly these two inputs.
    <div role="group" aria-label={label} className={cn('flex items-center gap-sm', className)}>
      <input
        id={swatchId}
        type="color"
        aria-label={`${label} (${t('color.swatch')})`}
        aria-describedby={describedById}
        // The native swatch cannot hold an invalid value, so it always shows the
        // last COMMITTED colour rather than the draft.
        value={value.toLowerCase()}
        disabled={disabled}
        onChange={(event) => {
          setDraft(null);
          onValueChange(event.target.value.toUpperCase());
        }}
        // Sized from the field scale rather than from a hardcoded `h-10 w-12`,
        // which made this the one kit control that could not be rendered at `sm`
        // (P10.2c, closing the pixel P10.2's done-when forbids).
        className={cn(
          fieldSquareVariants({ fieldSize: size }),
          'cursor-pointer p-1 disabled:cursor-not-allowed disabled:opacity-60',
        )}
      />
      <Input
        ref={ref}
        aria-label={`${label} (${t('color.hex')})`}
        aria-describedby={describedById}
        inputSize={size}
        value={draft ?? value}
        disabled={disabled}
        isInvalid={isInvalid || (draft !== null && !HEX_PATTERN.test(draft))}
        spellCheck={false}
        autoComplete="off"
        // Latin digits and letters regardless of the UI language — a hex colour
        // is not a localised number.
        dir="ltr"
        onChange={(event) => {
          commit(event.target.value);
        }}
        onBlur={() => {
          // Drop an unparseable draft on blur so the field settles back to the
          // truth instead of showing "#c9" forever.
          setDraft(null);
          onBlur?.();
        }}
        className="min-w-0 flex-1 font-mono"
      />
    </div>
  );
});
